import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Portal from 'core/components/Portal';
import SmartImage from 'core/components/SmartImage';

import { compose } from 'core/libs/recompose';

import modelPropTypes, { imageAttributes } from 'core/utils/prop-types/model';
import { callYaMetrikaMethod } from 'core/utils/counters-helper';

import withBreakpoint from 'core/hocs/withBreakpoint';
import withoutSSR from 'core/components/withoutSSR';
import Link from 'core/components/Link';

import { BASE_Z_INDEX } from 'core/site/constants';

import ScrollToSiteContext from './ScrollToSiteContext';

import styles from './index.styl';

export { default as checkScrollToSiteInTopic } from './checkScrollToSiteInTopic';
export { default as ScrollToSiteProvider } from './ScrollToSiteProvider';

export { ScrollToSiteContext };

const LOGO_HEIGHT = 60;

function ScrollToSite(props, context) {
  const {
    isMobile,
    content: {
      target_link: link,
      target_name: name,
      label_asset: logo,
      mobile_asset: mobileAsset,
      desktop_asset: desktopAsset,
    },
  } = props;

  const {
    counterIds,
    scrollToSite,
  } = context;

  const stickGoalName = scrollToSite?.stickGoalName;

  const [scrolled, setScrolled] = useState(false);
  const [percent, setPercent] = useState(0);

  const scrollTosite = useContext(ScrollToSiteContext);

  const rootEl = useRef(null);

  const isFullScrolled = percent >= 100;

  const onScroll = useCallback(() => {
    const windowHeight = document.documentElement.clientHeight;
    const rootTop = rootEl.current.getBoundingClientRect().top;

    setPercent(Math.trunc(100 * (windowHeight - rootTop) / windowHeight));

    if (rootTop < windowHeight) {
      if (!scrolled) {
        setScrolled(true);
      }
    } else {
      if (scrolled) {
        setScrolled(false);
      }
    }
  }, [scrolled]);

  const sendGoal = useCallback((goal = 'scroll-to-site _conversion') => () => {
    callYaMetrikaMethod(counterIds?.yandexMetrika, 'reachGoal', goal);
  }, [counterIds]);

  useEffect(() => {
    scrollTosite.setIsScrollToSite(true);
    return function cleanup() {
      scrollTosite.setIsScrollToSite(false);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    document.addEventListener('scroll', onScroll);
    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  useEffect(() => {
    if (isFullScrolled) {
      sendGoal()();
      document.location.assign(link);
    }
  }, [isFullScrolled, link, sendGoal]);

  /**
   * Расчитываем различные трансформации с некоторым коэфициентом
   * от процентов докрутки.
   */
  const scale = percent > 60 || percent <= 0
    ? 'none'
    : `scale(${(percent + 40) / 100})`;

  const opacity = percent < 40
    ? 0
    : (percent - 40) / 100;

  const translate = percent < 20
    ? 'none'
    : `translateY(${-(percent - 20)}px)`;

  const linkProps = {
    to: link,
    onClick: sendGoal(),
  };

  const screenshot = isMobile ? mobileAsset : desktopAsset;

  const logoSrc = logo?.attributes?.versions?.original;

  return (
    <Portal>
      <div
        className={cx(styles.scrollToSite, scrolled && styles.scrolled)}
        ref={rootEl}
        style={{ transform: translate, zIndex: BASE_Z_INDEX }}
      >
        <div className={styles.overlay} style={{ opacity }} />
        <div className={styles.stick}>
          <Link
            {...linkProps}
            className={styles.stickLink}
            {...stickGoalName && {
              onClick: sendGoal(stickGoalName),
            }}
          >
            <SmartImage
              width={Math.ceil(logoSrc?.width * LOGO_HEIGHT / logoSrc?.height)}
              height={LOGO_HEIGHT}
              src={logoSrc}
              placement='contain'
            />
          </Link>
        </div>
        <div className={styles.body}>
          <div className={styles.title}>
            Дальше вы перейдёте на сайт нашего партнера <Link {...linkProps}>{name}</Link>
          </div>
          <Link
            {...linkProps}
            className={styles.screenlink}
            style={{ transform: scale }}
          >
            <SmartImage src={screenshot?.attributes?.versions?.original} />
          </Link>
        </div>
      </div>
    </Portal>
  );
}

ScrollToSite.contextTypes = {
  counterIds: PropTypes.object,
  scrollToSite: PropTypes.object,
};

ScrollToSite.propTypes = {
  /** @ignore */
  isMobile: PropTypes.bool,
  content: PropTypes.shape({
    'target_link': PropTypes.string,
    'target_name': PropTypes.string,
    'label_asset': modelPropTypes(imageAttributes),
    'mobile_asset': modelPropTypes(imageAttributes),
    'desktop_asset': modelPropTypes(imageAttributes),
  }).isRequired,
  /** @ignore */
  location: PropTypes.object,
};

const ScrollToSiteWithHOC = compose(
  withoutSSR,
  withBreakpoint,
)(ScrollToSite);
ScrollToSiteWithHOC.displayName = 'ScrollToSite';

export { ScrollToSite as StorybookComponent };
export default ScrollToSiteWithHOC;
